import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { disconnectCalendar, getCalendar, saveSettings, startCalendarConnect } from '../api';
import type { CalendarInfo, CalendarSettings } from '../api';
import type { Session } from '../types';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const H2: CSSProperties = { fontSize: 18, margin: '0 0 4px' };
const CARD: CSSProperties = { marginBottom: 28 };
const ROW: CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
const CHECK: CSSProperties = { flexDirection: 'row', alignItems: 'center', gap: 4, fontWeight: 400 };
const LIST: CSSProperties = { margin: '4px 0 0', paddingLeft: 18, lineHeight: 1.7 };

function when(iso: string | null | undefined, tz?: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function CalendarTab({ session }: { session: Session }) {
  const [info, setInfo] = useState<CalendarInfo | null>(null);
  const [err, setErr] = useState('');
  const load = async () => {
    setInfo(await getCalendar(session));
  };
  useEffect(() => {
    load().catch((e) => setErr(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  if (!info) return <div className="empty">{err || 'Loading calendar...'}</div>;

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Estimate calendar</h1>
          <div className="sub">Connect your calendar, set when estimates can be booked, and see what's coming up.</div>
        </div>
      </div>
      <ConnectCard session={session} info={info} onChange={load} />
      <SettingsCard session={session} info={info} onSaved={load} />
      <UpcomingCard info={info} />
    </>
  );
}

function ConnectCard({ session, info, onChange }: { session: Session; info: CalendarInfo; onChange: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const connect = async () => {
    setBusy(true);
    setErr('');
    try {
      window.location.href = await startCalendarConnect(session, 'google');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start Google sign-in');
      setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect the calendar? The AI will stop booking estimates until you connect again.')) return;
    setBusy(true);
    setErr('');
    try {
      await disconnectCalendar(session);
      await onChange();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Disconnect failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="form" style={CARD}>
      <h2 style={H2}>Calendar connection</h2>
      {info.connected ? (
        <>
          <p style={{ margin: 0 }}>
            Connected to {info.provider === 'outlook' ? 'Outlook' : 'Google'} Calendar
            {info.connectedAt ? ` since ${when(info.connectedAt, info.settings.timezone)}` : ''}.
          </p>
          {info.calendarError && (
            <p className="err" style={{ margin: 0 }}>
              The calendar isn't answering: {info.calendarError}. Click Reconnect.
            </p>
          )}
          <div style={ROW}>
            <button className="primary" type="button" disabled={busy} onClick={() => void connect()}>Reconnect</button>
            <button className="ghost" type="button" disabled={busy} onClick={() => void disconnect()}>Disconnect</button>
          </div>
        </>
      ) : (
        <>
          <p style={{ margin: 0 }}>
            Not connected. Connect the Google Calendar where estimates should go. The AI checks it for open times and
            adds each booked estimate to it.
          </p>
          <div style={ROW}>
            <button className="primary" type="button" disabled={busy} onClick={() => void connect()}>
              {busy ? 'Opening Google...' : 'Connect Google Calendar'}
            </button>
          </div>
        </>
      )}
      <p className="sub" style={{ margin: 0 }}>
        While the Google app is in Testing, only approved test users can connect, Google shows an "unverified app"
        warning (click Continue), and the connection lasts 7 days before it needs Reconnect.
      </p>
      {err && <p className="err">{err}</p>}
    </div>
  );
}

function SettingsCard({ session, info, onSaved }: { session: Session; info: CalendarInfo; onSaved: () => Promise<void> }) {
  const [st, setSt] = useState<CalendarSettings>(info.settings);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => setSt(info.settings), [info.settings]);

  const set = (patch: Partial<CalendarSettings>) => {
    setSt({ ...st, ...patch });
    setMsg('');
  };
  const toggleDay = (d: number) => {
    const days = st.businessHours.days.includes(d) ? st.businessHours.days.filter((x) => x !== d) : [...st.businessHours.days, d];
    set({ businessHours: { ...st.businessHours, days: days.sort((a, b) => a - b) } });
  };
  const save = async () => {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      await saveSettings(session, {
        bookingEnabled: st.bookingEnabled,
        businessHours: st.businessHours,
        appointmentDurationMinutes: st.appointmentDurationMinutes,
        bookingBufferMinutes: st.bookingBufferMinutes,
        bookingLeadHours: st.bookingLeadHours,
        bookingDaysAhead: st.bookingDaysAhead,
        calendarId: st.calendarId,
      });
      await onSaved();
      setMsg('Saved. The next text uses this.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };
  const opts = (values: number[], fmt: (n: number) => string) => values.map((n) => <option key={n} value={n}>{fmt(n)}</option>);

  return (
    <div className="form" style={CARD}>
      <h2 style={H2}>Booking</h2>
      <label>
        AI booking
        <select value={st.bookingEnabled ? 'on' : 'off'} onChange={(e) => set({ bookingEnabled: e.target.value === 'on' })}>
          <option value="on">On. Once the required info is collected, the AI offers open times and books one.</option>
          <option value="off">Off. The AI collects info and says the team will follow up.</option>
        </select>
      </label>
      {st.bookingEnabled && !info.connected && (
        <p className="err" style={{ margin: 0 }}>Booking stays off until a calendar is connected.</p>
      )}
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Days estimates can be booked</div>
        <div style={ROW}>
          {DAYS.map((name, d) => (
            <label key={d} style={CHECK}>
              <input type="checkbox" checked={st.businessHours.days.includes(d)} onChange={() => toggleDay(d)} /> {name}
            </label>
          ))}
        </div>
      </div>
      <div style={ROW}>
        <label>
          From
          <input type="time" value={st.businessHours.start} onChange={(e) => set({ businessHours: { ...st.businessHours, start: e.target.value } })} />
        </label>
        <label>
          Until
          <input type="time" value={st.businessHours.end} onChange={(e) => set({ businessHours: { ...st.businessHours, end: e.target.value } })} />
        </label>
        <label>
          Estimate length
          <select value={st.appointmentDurationMinutes} onChange={(e) => set({ appointmentDurationMinutes: Number(e.target.value) })}>
            {opts([30, 45, 60, 90, 120], (n) => `${n} minutes`)}
          </select>
        </label>
        <label>
          Gap around other events
          <select value={st.bookingBufferMinutes} onChange={(e) => set({ bookingBufferMinutes: Number(e.target.value) })}>
            {opts([0, 15, 30, 45, 60], (n) => (n ? `${n} minutes` : 'None'))}
          </select>
        </label>
        <label>
          Minimum notice
          <select value={st.bookingLeadHours} onChange={(e) => set({ bookingLeadHours: Number(e.target.value) })}>
            {opts([0, 1, 2, 4, 12, 24, 48], (n) => (n ? `${n} hour${n === 1 ? '' : 's'}` : 'None'))}
          </select>
        </label>
        <label>
          Book up to
          <select value={st.bookingDaysAhead} onChange={(e) => set({ bookingDaysAhead: Number(e.target.value) })}>
            {opts([3, 5, 7, 10, 14], (n) => `${n} days ahead`)}
          </select>
        </label>
      </div>
      <label>
        Calendar ID ("primary" is your main calendar)
        <input value={st.calendarId} onChange={(e) => set({ calendarId: e.target.value })} />
      </label>
      <div className="sub">Times are in {st.timezone}.</div>
      {msg && <p>{msg}</p>}
      {err && <p className="err">{err}</p>}
      <button className="primary" type="button" disabled={busy} onClick={() => void save()}>
        {busy ? 'Saving...' : 'Save booking settings'}
      </button>
      {info.connected && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, marginTop: 8 }}>Next open times the AI can offer</div>
          {info.openTimes.length ? (
            <ul style={LIST}>{info.openTimes.map((t) => <li key={t.startIso}>{t.label}</li>)}</ul>
          ) : (
            <div className="sub">{info.calendarError ? 'Unavailable until the calendar is reconnected.' : 'No open times in this window. Try more days or hours.'}</div>
          )}
        </div>
      )}
    </div>
  );
}

function UpcomingCard({ info }: { info: CalendarInfo }) {
  const tz = info.settings.timezone;
  return (
    <div className="form" style={CARD}>
      <h2 style={H2}>Upcoming estimates</h2>
      {info.upcoming.length === 0 && <div className="sub">None booked yet.</div>}
      {info.upcoming.map((u) => (
        <div key={u.leadId} style={{ borderBottom: '1px solid #eee7da', padding: '8px 0' }}>
          <div style={{ fontWeight: 600 }}>{when(u.appointmentAt, tz)}</div>
          <div>
            {u.name || u.phone}
            {u.name && u.phone ? ` (${u.phone})` : ''}
            {u.service ? `: ${u.service}` : ''}
          </div>
          {u.address && <div className="sub">{u.address}</div>}
          {u.summary && <div className="sub">{u.summary}</div>}
        </div>
      ))}
    </div>
  );
}
